import * as React from 'react';

import { useQuery } from '@apollo/client';

import PartnerListContext from 'bundles/browse-common/contexts/PartnerListContext';
import { useSearchFacet } from 'bundles/browse-common/hooks/useSearchFacet';
import BrowseAllPartnersQuery from 'bundles/browse-common/queries/BrowseAllPartners.graphql';
import type {
  BrowseAllPartnersQuery as BrowseAllPartnersQueryReponse,
  BrowseAllPartnersQueryVariables,
  BrowseAllPartners_PartnerFragment as PartnerFragment,
} from 'bundles/browse-common/queries/__generated__/BrowseAllPartners';
import type { PartnerData } from 'bundles/browse-common/utils/experimentUtils';

type Props = {
  children: React.ReactNode;
};

const MAX_PARTNER_COUNT = 8;

const useQueryAllPartners = (skip: boolean) => {
  const { data, loading } = useQuery<BrowseAllPartnersQueryReponse, BrowseAllPartnersQueryVariables>(
    BrowseAllPartnersQuery,
    { skip, context: { clientName: 'gatewayGql' } }
  );
  return { data, loading };
};

const SkillValuePropProvider = ({ children }: Props) => {
  const { facet: partnersFacet, isLoading: isLoadingSearch } = useSearchFacet('partners');
  const { data, loading: isLoadingAllPartners } = useQueryAllPartners(!isLoadingSearch && !partnersFacet);

  const allPartnersMap = data?.Partner?.queryAll?.elements.reduce((acc, partner) => {
    acc[partner.name] = partner;
    return acc;
  }, {} as Record<string, PartnerFragment>);

  const partners: PartnerData[] = [];

  if (partnersFacet?.valuesAndCounts) {
    for (const partnerFromFacet of partnersFacet.valuesAndCounts) {
      if (partners.length >= MAX_PARTNER_COUNT) {
        break;
      }

      const partner = allPartnersMap?.[partnerFromFacet.value];
      const partnerLogo = partner?.rectangularLogo || partner?.logo || partner?.squareLogo;

      if (partner && partnerLogo && !partner.name.toLowerCase().includes('coursera')) {
        partners.push({
          relationSlug: partner.slug,
          partnerName: partner.name,
          imageAltText: partner.name,
          image: { url: partnerLogo },
        });
      }
    }
  }

  return (
    <PartnerListContext.Provider
      value={{
        partners,
        loading: isLoadingSearch || isLoadingAllPartners,
      }}
    >
      {children}
    </PartnerListContext.Provider>
  );
};

export default SkillValuePropProvider;
